import {Injectable} from "@angular/core";
import * as moment from "moment";
import * as _ from "lodash";

import {KendoDatetimeLocaleService} from "./kendo.datetime.service";
import {IDay} from "../../../app.services/scheduling.config/schedule.modality.groups.manager/interfaces/schedule.modality.availability.entry";

export interface IDateRange {
	fromDate: Date;
	toDate: Date;
}

export enum DateMode {
	Default = 'default',
	FullWeek = 'full-week',
	WorkWeek = 'work-week',
	SingleDay = 'single-day',
	ThreeDays = 'three-days',
	TwoWeeks = 'two-weeks',
	CustomRange = 'custom-range'
}

// export interface IDateTime {
// 	year: number;
// 	month: number;
// 	date: number;
// 	hour: number;
// 	minute: number;
// 	second: number;
// }

@Injectable({ providedIn: 'root' })
  export class DateHelperService {

	constructor(private kendoDatetimeLocaleService: KendoDatetimeLocaleService) {
	}

	/*
	 * Returns the first week day index starting from Sunday (0 == Sunday).
	 */
	public get firstWeekDay() {
		return this.kendoDatetimeLocaleService.firstDay();
	}

	/*
	* Get all week days dates by date
	* */
	public getAllWeekDaysDates(date: Date, dateMode: DateMode = DateMode.Default, weekDays: IDay[] = [], dateRange: IDateRange = {fromDate: null, toDate: null}): Date[] {

		let dateMoment = moment(date);
		let weekDates: Date[] = [];
		let _weekDays: IDay[] = this.sortWeekDays(weekDays);

		switch (dateMode) {
			case DateMode.FullWeek:
				for (let i = 0; i <= 6; i++) {
					weekDates.push(dateMoment.day(i).toDate());
				}
				break;

			case DateMode.WorkWeek:
				for (let i = 0; i <= 6; i++) {
					if (_weekDays[i] && !_weekDays[i].isWeekend) {
						weekDates.push(dateMoment.day(i).toDate());
					}
				}
				break;

			case DateMode.ThreeDays:
				weekDates.push(dateMoment.toDate());  //selected day
				weekDates.push(dateMoment.add(1, "day").toDate()); //a day after selected day
				weekDates.push(dateMoment.add(1, "day").toDate());// two days after selected day
				break;

			case DateMode.TwoWeeks:
				for (let i = 0; i <= 6; i++) {
					weekDates.push(dateMoment.day(i).toDate());
				}
				dateMoment.add(7, "day")
				for (let i = 0; i <= 6; i++) {
					weekDates.push(dateMoment.day(i).toDate());
				}
				break;

			case DateMode.CustomRange:
				if (dateRange.fromDate !== null) {
					if (dateRange.toDate === null) {
						weekDates.push(dateRange.fromDate);
					} else {
						weekDates.push(dateRange.fromDate);
						let loop = new Date(dateRange.fromDate);
						while (loop < dateRange.toDate) {
							let newDate = loop.setDate(loop.getDate() + 1);
							weekDates.push(new Date(newDate));
						}
					}
				}
				//
				// if (dateRange.fromDate === null) {
				// 	dateRange.fromDate = date;
				// 	dateRange.toDate = null;
				// 	weekDates.push(dateMoment.toDate());
				// } else {
				// 	dateRange.toDate = date;
				//
				// 	weekDates.push(dateRange.fromDate);
				// 	let loop = new Date(dateRange.fromDate);
				// 	while (loop < dateRange.toDate) {
				// 		let newDate = loop.setDate(loop.getDate() + 1);
				// 		weekDates.push(new Date(newDate));
				// 	}
				// }
				break;

			case DateMode.SingleDay:
			default:
				weekDates.push(dateMoment.toDate());
				break;
		}

		return weekDates;
	}

	/*
	* Get next date
	* */
	public getNextDate(date: Date, dateMode: DateMode = DateMode.Default, weekDays: IDay[] = []): Date {

		let nextDate: Date;
		let weekDates: Date[] = this.getAllWeekDaysDates(date, dateMode, weekDays);

		switch (dateMode) {
			case DateMode.FullWeek:
				nextDate = moment(weekDates[weekDates.length -1]).add(1, 'days').toDate();
				break;

			case DateMode.WorkWeek:
				nextDate = moment(weekDates[weekDates.length -1]).add(3, 'days').toDate();
				break;

			case DateMode.SingleDay:
			default:
				nextDate = moment(date).add( 1, "day").toDate();
				break;
		}

		return nextDate;
	}

	/*
	* Get previous date
	* */
	public getPreviousDate(date: Date, dateMode: DateMode = DateMode.Default, weekDays: IDay[] = []): Date {

		let previousDate: Date;
		let weekDates: Date[] = this.getAllWeekDaysDates(date, dateMode, weekDays);

		switch (dateMode) {
			case DateMode.FullWeek:
				previousDate = moment(weekDates[0]).subtract(7, 'days').toDate();
				break;

			case DateMode.WorkWeek:
				previousDate = moment(weekDates[0]).subtract(7, 'days').toDate();
				break;

			case DateMode.SingleDay:
			default:
				previousDate = moment(date).subtract( 1, "day").toDate();
				break;
		}

		return previousDate;
	}

	/*
	* Get week date range
	* */
	public getWeekDateRange(date: Date, dateMode: DateMode = DateMode.Default, weekDays: IDay[] = [], customDateRange: IDateRange = {fromDate: null, toDate: null}): IDateRange {

		let weekDates: Date[] = [];
		let dateRange: IDateRange;

		switch (dateMode) {
			case DateMode.FullWeek:
			case DateMode.WorkWeek:
			case DateMode.ThreeDays:
			case DateMode.TwoWeeks:
			case DateMode.CustomRange:
				weekDates = this.getAllWeekDaysDates(date, dateMode, weekDays, customDateRange);
				break;

			case DateMode.SingleDay:
			default:
				weekDates[0] = date;
				break;
		}

		dateRange = {
			fromDate: weekDates[0],
			toDate: weekDates[weekDates.length -1]
		};

		return dateRange;
	}

	/*
	* Format date range
	* */
	public formatDateRange(fromDate: Date, toDate: Date) {

		let fromMoment = moment(fromDate);
		let toMoment = moment(toDate);
		let formattedDateRange: string = "";

		if (fromMoment.isSame(toMoment, 'day')) {
			formattedDateRange = this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "DayOfMonth");
			formattedDateRange += " " + this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "MonthName");
			formattedDateRange += ", " + this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "YearLong");
		} else if (fromMoment.year() !== toMoment.year()) {
			formattedDateRange = this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "DayOfMonth");
			formattedDateRange += " " + this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "MonthName");
			formattedDateRange += ", " + this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "YearLong");
			formattedDateRange += " - " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "DayOfMonth");
			formattedDateRange += " " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "MonthName");
			formattedDateRange += ", " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "YearLong");
		} else if (fromMoment.month() !== toMoment.month()) {
			formattedDateRange = this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "DayOfMonth");
			formattedDateRange += " " + this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "MonthName");
			formattedDateRange += " - " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "DayOfMonth");
			formattedDateRange += " " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "MonthName");
			formattedDateRange += ", " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "YearLong");
		} else {
			formattedDateRange = this.kendoDatetimeLocaleService.date(fromMoment.toDate(), "DayOfMonth");
			formattedDateRange += " - " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "DayOfMonth");
			formattedDateRange += " " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "MonthName");
			formattedDateRange += ", " + this.kendoDatetimeLocaleService.date(toMoment.toDate(), "YearLong");
		}

		return formattedDateRange;
	}

	/*
	* Get week date marker class
	* */
	public getWeekDateMarkerClass(date: Date, selectedDate: Date, dateMode: DateMode = DateMode.Default, weekDays: IDay[] = [], customDateRange: IDateRange = {fromDate: null, toDate: null}): string {

		let isFound: boolean = false;
		let markerStylesClasses: string = "";
		let weekDates: Date[] = this.getAllWeekDaysDates(selectedDate, dateMode, weekDays, customDateRange);

		weekDates.forEach((d: Date) => {
			if (moment(d).isSame(date)) {
				isFound = true;
			}
		});

		if (isFound) {
			markerStylesClasses = "week-dates-marker ";

			if (dateMode !== DateMode.SingleDay) {
				if (moment(weekDates[0]).isSame(date) || moment(weekDates[weekDates.length - 1]).isSame(date)) {
					markerStylesClasses += "first-last ";
				}
			}

			switch (dateMode) {
				case DateMode.FullWeek:
					markerStylesClasses += DateMode.FullWeek;
					break;
				case DateMode.WorkWeek:
					markerStylesClasses += DateMode.WorkWeek;
					break;
				case DateMode.ThreeDays:
					markerStylesClasses += DateMode.ThreeDays;
					break;
				case DateMode.TwoWeeks:
					markerStylesClasses += DateMode.TwoWeeks;
					break;
				case DateMode.CustomRange:
					markerStylesClasses += DateMode.CustomRange;
					break;
				case DateMode.SingleDay:
					markerStylesClasses += DateMode.SingleDay;
					break;
				default:
					break;
			}
		}

		return markerStylesClasses.trim();
	}


	/*
	Checks whether the given dates are the same day (ignoring time)
	 */
	public isSameDates(date1: Date, date2: Date): boolean {
		let date1Moment = moment(date1);
		return date1Moment.isSame(date2, 'day');
	}

	/*
	Returns local date and time in provided time zone
	 */
	// public getLocalDateTimeByTimeZone(dateTime: Date, timeZone: number): IDateTime {
	// 	let res = new Date(dateTime.getTime() + timeZone*60*60*1000);
	// 	return  {
	// 		year: res.getUTCFullYear(),
	// 		month: res.getUTCMonth(),
	// 		date: res.getUTCDate(),
	// 		hour: res.getUTCHours(),
	// 		minute: res.getUTCMinutes(),
	// 		second: res.getUTCSeconds()
	// 	};
	// }

	/*
	Returns provided date and time in required time zone in specified format
	 */
	public getLocalDateTimeByStringTimeZone(dateTime: Date, timeZone: string): string {
		return dateTime.toLocaleString("en-US", {timeZone: timeZone});
	}

	/*
	Returns provided date in required time zone in specified format
	 */
	public getLocalDateByStringTimeZone(dateTime: Date, timeZone: string): string {
		return dateTime.toLocaleDateString("en-US", {timeZone: timeZone});
	}

	/*
	Returns provided time in required time zone in specified format
	 */
	public getLocalTimeByStringTimeZone(dateTime: Date, timeZone: string): string {
		return dateTime.toLocaleTimeString("en-US", {timeZone: timeZone});
	}

	/*
	* Sort weekdays / complete missing weekdays
	* note: weekDays array might contain les than 7 days, so we need to fill the missing days
	* */
	private sortWeekDays(weekDays: IDay[] = []) {

		let _weekDays: IDay[] = [];

		if (weekDays.length === 7) {
			_weekDays = _.sortBy(weekDays, ['weekDayIndex']);
		} else {
			// If week days array less than 7 days, then complete the missing days
			for (let i = 0; i <= 6; i++) {
				for (let j = 0; j < weekDays.length; j++) {
					if (i === weekDays[j].weekDayIndex) {
						_weekDays[i] = weekDays[j];
					}
				}
				if (!_weekDays[i]) {
					_weekDays[i] = null;
				}
			}
		}

		return _weekDays;
	}
}
